import type { Staff, Store } from "@ai-pos/shared";
import { create } from "zustand";
import { authApi } from "../api/resources.js";

interface AuthState {
  staff: Staff | null;
  activeStoreId: string | null;
  storeName: string;
  accessibleStores: Store[];
  loading: boolean;
  error: string | null;
  login: (pin: string) => Promise<boolean>;
  logout: () => Promise<void>;
  restoreSession: () => Promise<void>;
  switchStore: (storeId: string) => Promise<boolean>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  staff: null,
  activeStoreId: null,
  storeName: "",
  accessibleStores: [],
  loading: true,
  error: null,
  login: async (pin: string) => {
    set({ error: null });
    try {
      const res = await authApi.login(pin);
      set({
        staff: res.staff,
        activeStoreId: res.activeStoreId,
        storeName: res.storeName,
        accessibleStores: res.stores,
      });
      return true;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : "ログインに失敗しました" });
      return false;
    }
  },
  logout: async () => {
    await authApi.logout().catch(() => undefined);
    set({ staff: null, activeStoreId: null, storeName: "", accessibleStores: [] });
  },
  restoreSession: async () => {
    try {
      const res = await authApi.me();
      set({
        staff: res.staff,
        activeStoreId: res.activeStoreId,
        storeName: res.storeName,
        accessibleStores: res.stores,
        loading: false,
      });
    } catch {
      set({ staff: null, activeStoreId: null, storeName: "", accessibleStores: [], loading: false });
    }
  },
  switchStore: async (storeId: string) => {
    try {
      const res = await authApi.switchStore(storeId);
      set({ activeStoreId: res.activeStoreId, storeName: res.storeName });
      return true;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : "店舗の切り替えに失敗しました" });
      return false;
    }
  },
  clearError: () => set({ error: null }),
}));
